import type { LeadInteraction } from '@/types';

import React from 'react';
import { Card, CardBody, Button, Chip } from '@heroui/react';
import { RiDeleteBinLine, RiPhoneLine, RiMailLine, RiCalendarLine, RiWhatsappLine } from '@remixicon/react';

import { UserCell } from '@/components/shared/UserCell';
import { DateTimeDisplay } from '@/components/shared/DateTimeDisplay';
import { PermissionGuard } from '@/components/shared/PermissionGuard';

interface LeadInteractionItemProps {
  interaction: LeadInteraction;
  onDelete: () => void;
  isDeleting?: boolean;
}

const INTERACTION_ICONS = {
  call: RiPhoneLine,
  email: RiMailLine,
  meeting: RiCalendarLine,
  whatsapp: RiWhatsappLine
};

const INTERACTION_COLORS = {
  call: 'primary',
  email: 'secondary',
  meeting: 'success',
  whatsapp: 'success'
} as const;

export function LeadInteractionItem({ interaction, onDelete, isDeleting }: LeadInteractionItemProps) {
  const Icon = INTERACTION_ICONS[interaction.type];
  const color = INTERACTION_COLORS[interaction.type];

  return (
    <Card radius="sm" shadow="sm">
      <CardBody className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 space-y-2">
            <div className="flex items-center gap-2">
              <Icon className="h-4 w-4" />
              <Chip size="sm" color={color} variant="flat">
                {interaction.type.charAt(0).toUpperCase() + interaction.type.slice(1)}
              </Chip>
            </div>
            <p className="text-default-700 whitespace-pre-wrap">{interaction.note}</p>
            {interaction.outcome && (
              <div className="bg-success-50 text-success-700 dark:bg-success-900/20 dark:text-success-400 rounded-md p-2 text-sm">
                <strong>Outcome:</strong> {interaction.outcome}
              </div>
            )}
            <div className="text-default-500 flex items-center gap-4 text-sm">
              <UserCell user={interaction.user} dense />
              <DateTimeDisplay dateString={interaction.created_at} />
            </div>
          </div>
          <PermissionGuard permission="LeadInteraction.destroy">
            <Button isIconOnly size="sm" variant="light" color="danger" onPress={onDelete} isLoading={isDeleting}>
              <RiDeleteBinLine className="h-4 w-4" />
            </Button>
          </PermissionGuard>
        </div>
      </CardBody>
    </Card>
  );
}
